import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useState, useEffect } from 'react';
import Dashboard      from './pages/Dashboard';
import DiveList       from './pages/DiveList';
import DiveDetail     from './pages/DiveDetail';
import MapPage        from './pages/MapPage';
import ImportPage     from './pages/ImportPage';
import LoginPage      from './pages/LoginPage';
import PublicLogbook  from './pages/PublicLogbook';
import SpeciesPage    from './pages/SpeciesPage';
import TripsPage      from './pages/TripsPage';
import BrevetPage        from './pages/BrevetPage';
import VerzekeringPage  from './pages/VerzekeringPage';
import './App.css';

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on  = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

const NAV = [
  { to: '/',        icon: '🏠', label: 'Home'         },
  { to: '/dives',   icon: '📋', label: 'Logboek'      },
  { to: '/map',     icon: '🗺️', label: 'Kaart'        },
  { to: '/trips',   icon: '✈️', label: 'Reizen'       },
  { to: '/brevets',       icon: '🎓', label: 'Brevetten'    },
  { to: '/verzekeringen', icon: '🛡️', label: 'Verzekeringen' },
  { to: '/species', icon: '🐟', label: 'Soorten'      },
  { to: '/import',  icon: '⚙️', label: 'Instellingen' },
];

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Laden…</div>;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const online = useOnlineStatus();

  return (
    <div className="layout">

      {/* Desktop sidebar */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-name">DIVE LOGBOOK</span>
        </div>
        <nav className="sidebar-nav">
          {NAV.map(n => {
            if (n.to === '/import' && !isAdmin) return null;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === '/'}
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                {n.icon} {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div style={{ marginBottom: 6, fontWeight: 600, color: 'var(--text)' }}>
            {user?.username}
            {isAdmin && (
              <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--blue-light)', color: 'var(--blue)', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>
                ADMIN
              </span>
            )}
          </div>
          <button onClick={logout}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, padding: 0 }}>
            Uitloggen →
          </button>
          <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', opacity: 1 }}>
            v2.2.0
          </div>
        </div>
      </aside>

      {/* Offline banner */}
      {!online && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9000,
          background: '#f59e0b', color: '#fff', textAlign: 'center',
          padding: '8px 16px', fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          📵 Offline — je bekijkt gecachede data
        </div>
      )}

      {/* Pagina inhoud */}
      <main className="main-content" style={!online ? { paddingTop: 36 } : {}}>
        <Routes>
          <Route path="/"          element={<Dashboard />} />
          <Route path="/dives"     element={<DiveList />} />
          <Route path="/dives/:id" element={<DiveDetail />} />
          <Route path="/map"       element={<MapPage />} />
          <Route path="/trips"     element={<TripsPage />} />
          <Route path="/brevets"       element={<BrevetPage />} />
          <Route path="/verzekeringen" element={<VerzekeringPage />} />
          <Route path="/species"   element={<SpeciesPage />} />
          {isAdmin && <Route path="/import" element={<ImportPage />} />}
          <Route path="*"          element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Mobiele bottom navigation */}
      <nav className="bottom-nav">
        {NAV.map(n => {
          if (n.to === '/import' && !isAdmin) return null;
          return (
            <NavLink key={n.to} to={n.to} end={n.to === '/'}
              className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}>
              <span className="nav-icon">{n.icon}</span>
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>

    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login"          element={<LoginPage />} />
          <Route path="/share/:token"   element={<PublicLogbook />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
